/* @flow */

import logger from '../app/logger';

import { reportError } from './helpers';

/**
 * Parses the query/search or fragment/hash parameters out of a specific URL and
 * returns them as a JS object.
 *
 * @param {URL} url - The URL to parse.
 * @param {boolean} dontParse - If falsy, some transformations (for parsing the
 * value as JSON) will be executed.
 * @param {string} source - If {@code 'search'}, the parameters will parsed out
 * of {@code url.search}; otherwise, out of {@code url.hash}.
 * @returns {Object}
 */
export function parseURLParams(
        url: URL,
        dontParse: boolean = false,
        source: string = 'hash'): Object {

    const paramStr = source === 'search' ? url.search : url.hash;
    const params = {};
    const paramParts = (paramStr && paramStr.substr(1).split('&')) || [];

    // Detect and ignore hash params for hash routers.
    if (source === 'hash' && paramParts.length === 1) {
        const firstParam = paramParts[0];

        if (firstParam.startsWith('/') && firstParam.split('&').length === 1) {
            return params;
        }
    }

    paramParts.forEach(part => {
        const param = part.split('=');
        const key = param[0];

        if (!key) {
            return;
        }

        let value;

        try {
            value = param[1];

            if (!dontParse) {
                let decoded = decodeURIComponent(value).replace(/\\&/, '&');

                // 🔧 Strip surrounding quotes if present
                if ((decoded.startsWith('"') && decoded.endsWith('"'))
                    || (decoded.startsWith("'") && decoded.endsWith("'"))) {
                    decoded = decoded.slice(1, -1);
                }

                value = decoded === 'undefined'
                    ? undefined
                    : checkIfUrl(decoded)
                        ? decoded
                        : safeJsonParse(decoded);
            }
        } catch (e) {
            reportError(
                e, `Failed to parse URL parameter value: ${String(value)}`);

            return;
        }

        params[key] = value;
    });

    return params;
}

/**
 * Save Parsing JSON
 *
 * @param {string} value
 * @returns {Object}
 */
function safeJsonParse(value: string): any {
    try {
        // Only parse if it’s a simple JSON-compatible value
        if (
            value === 'true'
            || value === 'false'
            || value === 'null'
            || /^-?\d+(\.\d+)?$/.test(value)
        ) {
            return JSON.parse(value);
        }
    } catch (e) {
        logger.error(
            'Failed to safeJsonParse JSON value:', value, e);
    }

    return value;
}

/** .........
 * Checking wether URL is exists or not.
 *
 *
 * @param {URL} url - The URL to parse.
 * @returns {boolean}
 */
export function checkIfUrl(url) {
    if (!url) {
        return false;
    }
    let ifUrl;

    try {
        ifUrl = new URL(url);
    } catch {
        ifUrl = null;
    }
    if (ifUrl && typeof ifUrl === 'object') {
        return true;
    }

    return false;
}
